// frontend/src/contexts/AuthContext.jsx
// FRONTEND_CODE_VERSION = "v1.0.8" // Added global 401 error handling
// Purpose: Provides authentication state and functions to React components.

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import {
  logout as apiLogout,
  login as apiLogin,
  jwtLogin as apiJwtLogin,
} from "../services/auth";
import { getApiBaseUrl } from "../config/api.js";
import analytics from "../utils/analytics";
import {
  hasPermission,
  hasMinimumRole,
  canAccessAdmin,
  canAccessModerator,
  getRoleDisplayName,
  getUserRoleLevel,
} from "../utils/rbac";
import { useTranslation } from "react-i18next";
import { getValidatedPendingTrackItems, setValidatedPendingTrackItems } from "../utils/localStorageValidation";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  // userPassword state removed - passwords are not stored for security reasons
  const [jwtToken, setJwtToken] = useState(null);
  const [authMethod, setAuthMethod] = useState(null); // 'jwt' or 'basic'
  const [isLoading, setIsLoading] = useState(true); // Initial session check loading
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Login attempt in progress
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [user, setUser] = useState(null); // RBAC user data
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Prevent multiple logout calls
  const [loginError, setLoginError] = useState(""); // Global login error state
  const lastExtensionRef = useRef(0);

  // Debug logging function
  const debugLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    console.log(`[AuthContext ${timestamp}] ${message}`, data || "");
  };

  // Global 401 error handler
  const handle401Error = (url = null) => {
    if (isLoggingOut) {
      debugLog("Already logging out, skipping duplicate logout");
      return;
    }

    // Skip redirect for admin endpoints - unauthenticated users are expected to get 401s
    // These endpoints are admin-only and shouldn't trigger login redirects
    if (
      url &&
      (url.includes("/api/admin/") ||
        url.includes("/api/rbac/") ||
        url.includes("/api/user/"))
    ) {
      debugLog(`Skipping 401 redirect for admin endpoint: ${url}`);
      return;
    }

    debugLog("=== 401 Unauthorized detected - Auto-logging out ===");
    setIsLoggingOut(true);

    // Clear session immediately
    clearStoredSession();

    // Show user-friendly message before redirect
    console.log("Session expired. Redirecting to login...");

    // Redirect to login page with a small delay to ensure cleanup
    setTimeout(() => {
      window.location.href = "/login";
    }, 100);
  };

  // Set up global 401/422 error listener
  useEffect(() => {
    const handleGlobalError = (event) => {
      if (
        event.detail &&
        (event.detail.status === 401 || event.detail.status === 422)
      ) {
        const url = event.detail.url || event.detail.config?.url || "";
        debugLog(
          `Global ${event.detail.status} error detected via custom event for URL: ${url}`
        );
        handle401Error(url);
      }
    };

    // Listen for custom 401 events
    window.addEventListener("auth401Error", handleGlobalError);

    // Also intercept fetch/axios errors globally
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (response.status === 401 || response.status === 422) {
          // Extract URL from fetch arguments (first arg is URL)
          const url =
            args[0]?.toString() ||
            (typeof args[0] === "string" ? args[0] : args[0]?.url) ||
            "";
          debugLog(
            `Fetch ${response.status} error detected (JWT validation failure) for URL: ${url}`
          );
          handle401Error(url);
        }
        return response;
      } catch (error) {
        // Also check for 401/422 in network errors
        if (
          error.message &&
          (error.message.includes("401") || error.message.includes("422"))
        ) {
          const url =
            args[0]?.toString() ||
            (typeof args[0] === "string" ? args[0] : args[0]?.url) ||
            "";
          debugLog(`Network error with 401/422 detected for URL: ${url}`);
          handle401Error(url);
        }
        throw error;
      }
    };

    return () => {
      window.removeEventListener("auth401Error", handleGlobalError);
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, isLoggingOut]);

  // Check if session is expired
  const isSessionExpired = () => {
    if (!sessionExpiry) return true;
    const expired = new Date() > new Date(sessionExpiry);
    debugLog(`Session expiry check: ${sessionExpiry}, expired: ${expired}`);
    return expired;
  };

  // Get session duration based on remember me preference
  const getSessionDuration = () => {
    // TESTING: Use very short sessions for testing (remove in production)
    const isTestMode = localStorage.getItem("testMode") === "true";
    if (isTestMode) {
      return rememberMe ? 2 : 1; // 2 minutes for remember me, 1 minute for normal
    }

    return rememberMe ? 7 * 24 : 24; // 7 days for remember me, 1 day for normal
  };

  // Validate stored session
  const validateSession = async () => {
    debugLog("=== Starting session validation ===");
    const storedEmail = localStorage.getItem("userEmail");
    const storedJwtToken = localStorage.getItem("jwtToken");
    // Password is NOT retrieved from localStorage (security)
    const storedExpiry = localStorage.getItem("sessionExpiry");
    const storedRememberMe = localStorage.getItem("rememberMe") === "true";
    const storedAuthMethod = localStorage.getItem("authMethod");

    debugLog("Stored session data:", {
      email: storedEmail,
      hasJwtToken: !!storedJwtToken,
      expiry: storedExpiry,
      rememberMe: storedRememberMe,
      authMethod: storedAuthMethod,
    });

    if (!storedEmail || !storedExpiry) {
      debugLog("No stored session found - missing email or expiry");
      setIsLoading(false);
      return false;
    }

    // Check if session is expired
    if (new Date() > new Date(storedExpiry)) {
      debugLog("Stored session is expired");
      clearStoredSession();
      setIsLoading(false);
      return false;
    }

    try {
      debugLog("Validating stored session...");

      if (storedAuthMethod === "jwt" && storedJwtToken) {
        // For JWT, we can't easily validate without making a request
        // Just check if token exists and session hasn't expired
        debugLog("JWT session validation successful");
        setIsAuthenticated(true);
        setUserEmail(storedEmail);
        setJwtToken(storedJwtToken);
        setAuthMethod("jwt");
        setSessionExpiry(storedExpiry);
        setRememberMe(storedRememberMe);

        // Restore RBAC user data from localStorage
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData);
            setUser(userData);
            debugLog("RBAC user data restored from localStorage", userData);
            
            // Apply user's language preference on session restore
            if (userData.language && (userData.language === "he" || userData.language === "en")) {
              debugLog("Applying user's language preference on session restore", userData.language);
              i18n.changeLanguage(userData.language);
            }
          } catch (error) {
            debugLog("Error parsing stored user data", error.message);
          }
        }

        setIsLoading(false);
        debugLog("JWT session restored successfully");
        return true;
      } else {
        // No valid JWT token - require re-login (password storage removed for security)
        debugLog("No valid JWT token found. User must log in again.");
        debugLog("Invalid stored session data");
        clearStoredSession();
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      debugLog("Session validation error:", error.message);
      clearStoredSession();
      setIsLoading(false);
      return false;
    }
  };

  // Clear stored session data
  const clearStoredSession = () => {
    debugLog("Clearing stored session data");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userPassword");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("sessionExpiry");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("authMethod");
    localStorage.removeItem("userData"); // RBAC user data
    setIsAuthenticated(false);
    setUserEmail(null);
    // userPassword state removed - passwords are not stored
    setJwtToken(null);
    setAuthMethod(null);
    setSessionExpiry(null);
    setRememberMe(false);
    setUser(null); // Clear RBAC user data
    debugLog("Session data cleared from state and localStorage");
  };

  // Initialize session on mount
  useEffect(() => {
    debugLog("AuthContext (v1.0.7): Initializing session validation");
    validateSession();
  }, []);

  // Set up session expiry check
  useEffect(() => {
    if (!sessionExpiry) return;

    const checkExpiry = () => {
      if (isSessionExpired()) {
        debugLog("Session expired, logging out");
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [sessionExpiry]);

  // Post-signup auto-tracking for unavailable favorites
  useEffect(() => {
    if (isAuthenticated && user) {
      const pendingItems = getValidatedPendingTrackItems();
      
      if (pendingItems.length > 0) {
        // Import trackItem from services
        import('../services/tracking').then(({ trackItem }) => {
          // Track items that user wanted to track
          Promise.all(
            pendingItems.map(itemId => trackItem(itemId).catch(err => {
              console.error(`Failed to track item ${itemId}:`, err);
              return null;
            }))
          ).then(() => {
            // Clear pending items
            localStorage.removeItem('pending_track_items');
            
            // Track conversion completion
            import('../utils/analytics').then(({ trackEvent }) => {
              trackEvent('unavailable_favorites_auto_tracked', {
                item_count: pendingItems.length
              });
            });
            
            // Show success notification (if notification context is available)
            // Note: This will be handled by the component that uses this context
            console.log(`Auto-tracked ${pendingItems.length} items after sign-up`);
          });
        });
      }
    }
  }, [isAuthenticated, user]);

  const login = async (
    email,
    password,
    rememberMeOption = false,
    useJwt = true
  ) => {
    try {
      debugLog("=== Starting login process ===", {
        email,
        rememberMe: rememberMeOption,
        useJwt,
      });

      setLoginError(""); // Clear any previous login errors
      setIsLoggingIn(true); // Use separate state for login attempts

      let response;
      if (useJwt) {
        debugLog("Attempting JWT login...");
        response = await apiJwtLogin(email, password);
      } else {
        debugLog("Attempting Basic Auth login...");
        response = await apiLogin(email, password);
      }

      debugLog("Login response received:", {
        success: response.success,
        hasAccessToken: !!(response.data && response.data.access_token),
        message: response.message,
      });

      if (response.success) {
        debugLog("Login successful, setting up session");

        // Track login in Umami Analytics
        analytics.login(useJwt ? "jwt" : "basic");

        // Set session expiry based on remember me preference
        const duration = rememberMeOption ? 7 * 24 : 24; // 7 days or 1 day
        const expiry = new Date();
        expiry.setHours(expiry.getHours() + duration);

        debugLog("Setting authentication state...");
        setIsAuthenticated(true);
        setUserEmail(email);
        setSessionExpiry(expiry.toISOString());
        setRememberMe(rememberMeOption);

        if (useJwt && response.data.access_token) {
          // JWT authentication
          debugLog("Setting up JWT authentication");
          setJwtToken(response.data.access_token);
          setAuthMethod("jwt");
          localStorage.setItem("jwtToken", response.data.access_token);
          localStorage.setItem("authMethod", "jwt");
          debugLog("JWT token stored in localStorage");

          // Fetch RBAC user data
          try {
            debugLog("Fetching RBAC user data...");
            const userResponse = await fetch(
              `${getApiBaseUrl()}/api/rbac/user-info`,
              {
                headers: {
                  Authorization: `Bearer ${response.data.access_token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (userResponse.ok) {
              const userData = await userResponse.json();
              if (userData.success && userData.user) {
                debugLog("RBAC user data fetched successfully", userData.user);
                setUser(userData.user);
                localStorage.setItem("userData", JSON.stringify(userData.user));
                
                // Apply user's language preference only if no URL parameter is present
                // URL parameters should take precedence over user preferences
                const urlParams = new URLSearchParams(window.location.search);
                const urlLang = urlParams.get("lang");
                if (!urlLang && userData.user.language && (userData.user.language === "he" || userData.user.language === "en")) {
                  debugLog("Applying user's language preference", userData.user.language);
                  i18n.changeLanguage(userData.user.language);
                } else if (urlLang) {
                  debugLog("URL language parameter present, skipping user preference", { urlLang, userLang: userData.user.language });
                }
              }
            } else {
              debugLog("Failed to fetch RBAC user data", userResponse.status);
            }
          } catch (error) {
            debugLog("Error fetching RBAC user data", error.message);
          }
        } else {
          // Basic authentication (deprecated - password NOT stored for security)
          debugLog("Setting up Basic Auth authentication (deprecated - use JWT instead)");
          setAuthMethod("basic");
          localStorage.setItem("authMethod", "basic");
          // Password is NOT stored for security reasons
        }

        // Store common data
        localStorage.setItem("userEmail", email);
        localStorage.setItem("sessionExpiry", expiry.toISOString());
        localStorage.setItem("rememberMe", rememberMeOption.toString());

        debugLog("Login process completed successfully", {
          isAuthenticated: true,
          authMethod: useJwt ? "jwt" : "basic",
          hasJwtToken: !!(response.data && response.data.access_token),
          hasUserData: !!user,
        });

        setIsLoggingIn(false); // Clear login loading state
        setIsLoading(false); // Also clear initial loading if it was still set
        return { success: true };
      } else {
        debugLog("Login failed", response.message);
        setLoginError(response.message); // Set global error state
        setIsLoggingIn(false); // Clear login loading state
        return {
          success: false,
          error: response.message,
          verification_required: response.verification_required || false,
        };
      }
    } catch (error) {
      debugLog("Login error:", error.message);
      setIsLoggingIn(false); // Clear login loading state
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    debugLog("=== Logging out user ===");
    setIsLoggingOut(true);
    clearStoredSession();
    apiLogout();
    setIsLoggingOut(false);
  };

  // RBAC Helper Functions
  const hasPermissionHelper = (permission) => {
    return user ? hasPermission(user, permission) : false;
  };

  const hasMinimumRoleHelper = (minRole) => {
    return user ? hasMinimumRole(user, minRole) : false;
  };

  const canAccessAdminHelper = () => {
    return user ? canAccessAdmin(user) : false;
  };

  const canAccessModeratorHelper = () => {
    return user ? canAccessModerator(user) : false;
  };

  // FIX: Avoid recursive call by renaming this helper
  const getRoleDisplayNameHelper = () => {
    return user ? getRoleDisplayName(user) : "Unknown";
  };

  const getUserRoleLevelHelper = () => {
    return getUserRoleLevel(user);
  };

  // Get authentication headers for API requests
  const getAuthHeaders = () => {
    // Use JWT only (password storage removed for security)
    if (jwtToken) {
      debugLog("Using JWT headers for API request");
      return {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      };
    } else {
      debugLog("No JWT token available. User must log in again.");
      return {
        "Content-Type": "application/json",
      };
    }
  };

  // Extend session (throttled to prevent excessive calls)
  const extendSession = async () => {
    if (!isAuthenticated || !sessionExpiry || isExtendingSession) return;

    // Throttle: only allow extension every 5 minutes
    const now = Date.now();
    if (now - lastExtensionRef.current < 5 * 60 * 1000) {
      debugLog("Session extension throttled");
      return;
    }

    // JWT token refresh requires re-login (password is not stored for security)
    // If JWT expires, user must log in again
    debugLog("Cannot extend session without stored password. User must log in again.", {
      userEmail: userEmail ? "present" : "missing",
      currentExpiry: sessionExpiry,
    });
    
    // Set login error to inform user they need to log in again
    setLoginError(
      "Your session has expired. Please log in again to continue."
    );
    
    setIsExtendingSession(false);
  };

  // Debug: Log state changes
  useEffect(() => {
    debugLog("Authentication state changed:", {
      isAuthenticated,
      userEmail,
      hasJwtToken: !!jwtToken,
      authMethod,
      isLoading,
    });
  }, [isAuthenticated, userEmail, jwtToken, authMethod, isLoading]);

  // Add debug logging for user role/permissions
  useEffect(() => {
    if (user) {
      // eslint-disable-next-line no-console
      console.debug("[RBAC DEBUG] AuthContext user:", {
        email: user.email,
        role: user.role,
        role_level: user.role_level,
        is_superuser: user.is_superuser,
        permissions: user.permissions,
      });
    }
  }, [user]);

  const clearLoginError = () => {
    setLoginError("");
  };

  const contextValue = {
    isAuthenticated,
    userEmail,
    // userPassword removed - passwords are not stored for security reasons
    jwtToken,
    authMethod,
    login,
    logout,
    isLoading,
    sessionExpiry,
    extendSession,
    isExtendingSession,
    rememberMe,
    getSessionDuration,
    loginError, // Add global login error to context
    // RBAC data and functions
    user,
    hasPermission: hasPermissionHelper,
    hasMinimumRole: hasMinimumRoleHelper,
    canAccessAdmin: canAccessAdminHelper,
    canAccessModerator: canAccessModeratorHelper,
    getRoleDisplayName: getRoleDisplayNameHelper,
    getUserRoleLevel: getUserRoleLevelHelper,
    getAuthHeaders: getAuthHeaders, // Add getAuthHeaders to context value
    clearLoginError, // Add clearLoginError to context value
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    console.error("useAuth must be used within an AuthProvider");
    return {
      isAuthenticated: false,
      userEmail: null,
      // userPassword removed - passwords are not stored for security reasons
      jwtToken: null,
      authMethod: null,
      login: () => console.warn("Login function called outside AuthProvider"),
      logout: () => console.warn("Logout function called outside AuthProvider"),
      isLoading: false,
      sessionExpiry: null,
      extendSession: () => {},
      isExtendingSession: false,
      rememberMe: false,
      getSessionDuration: () => 24,
      // RBAC fallbacks
      user: null,
      hasPermission: () => false,
      hasMinimumRole: () => false,
      canAccessAdmin: () => false,
      canAccessModerator: () => false,
      getRoleDisplayName: () => "Unknown",
      getUserRoleLevel: () => 0,
      getAuthHeaders: () => ({}),
      clearLoginError: () => {},
    };
  }
  return context;
};

export { AuthContext };
